import type { SendEventParams } from '@/utils/sendEvent';
import { PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';
import { getCoreVisitorId } from '@/core';

export interface IPurchaseData {
  value: number;
  currency: string;
  metadata?: Record<string, unknown>;
  transactionId?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category?: string;
    quantity: number;
    price: number;
  }>;
}

/**
 * Tracker para eventos de compra
 */
export class PurchaseTracker extends BaseTracker {
  /**
   * Retorna o payload para envio centralizado no ConversionEngine.
   */
  public getPayload(data: IPurchaseData): SendEventParams {
    this.validateRequiredParams(
      { value: data.value, currency: data.currency },
      ['value', 'currency']
    );
    const { value, currency, transactionId, items, metadata } = data;
    return {
      category: 'ECOMMERCE',
      type: 'PURCHASE',
      eventData: {
        value,
        currency,
        transactionId,
        items,
        ...(metadata ? { metadata } : {}),
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
      visitorId: this.visitorId,
    };
  }

  /**
   * Rastreia uma compra
   */
  public async track({
    value,
    currency,
    transactionId,
    items,
  }: IPurchaseData): Promise<PixelResponse> {
    this.validateRequiredParams({ value, currency }, ['value', 'currency']);
    return this.sendEvent(
      this.getPayload({ value, currency, transactionId, items })
    );
  }

  /**
   * Rastreia uma compra com parâmetros adicionais
   */
  public async trackWithCustomParams(
    purchaseData: IPurchaseData,
    customParams: Record<string, unknown>
  ): Promise<PixelResponse> {
    this.validateRequiredParams(
      { value: purchaseData.value, currency: purchaseData.currency },
      ['value', 'currency']
    );

    return this.sendEvent({
      category: 'ECOMMERCE',
      type: 'PURCHASE',
      eventData: {
        value: purchaseData.value,
        currency: purchaseData.currency,
        transactionId: purchaseData.transactionId,
        items: purchaseData.items,
        ...customParams,
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
      visitorId: this.visitorId,
    });
  }
}

// Função de conveniência para compatibilidade com código existente
export const purchase = ({
  value,
  currency,
  transactionId,
  items,
}: IPurchaseData): Omit<
  SendEventParams,
  'config' | 'sessionId' | 'version'
> => {
  const visitorId = getCoreVisitorId();
  if (!visitorId) {
    throw new Error('Visitor ID não encontrado');
  }
  return {
    category: 'ECOMMERCE',
    type: 'PURCHASE',
    eventData: {
      value,
      currency,
      transactionId,
      items,
    },
    visitorId: visitorId,
  };
};
